import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateCallStatusRequestSchema,
  type CreateCallStatusRequest,
  type CallStatusDetail,
  type CallStatusListItem,
  UpdateCallStatusRequestSchema,
  type UpdateCallStatusRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateCallStatus, useUpdateCallStatus } from '../use-call-statuses';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';

interface CallStatusFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  callStatus?: CallStatusDetail | CallStatusListItem | null;
}

// `isConnected` was retired from the form (see ADR follow-up):
// granular outcomes now live on the CallSubStatus child table.
// We still send `isConnected: false` here so the server's create
// schema is satisfied; existing DB rows keep their stored value
// untouched and edit-mode leaves the column alone.
const CREATE_DEFAULTS: CreateCallStatusRequest = {
  code: '',
  name: '',
  description: '',
  isConnected: false,
  sortOrder: 0,
  isActive: true,
};

/**
 * Field set shared by create + edit. 2026-06-25: the
 * "Update Query Status" target pickers were removed - status
 * changes go through `LeadStatusChanger` (ADR-0054) now.
 */
const CallStatusFields = ({ pending }: { pending: boolean }): React.JSX.Element => {
  return (
    <>
      <UzFormField name="name">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Name"
            placeholder="e.g. Answered"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="description">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Description"
            placeholder="Optional"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="sortOrder">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Sort order"
            precision={0}
            value={field.value === null || field.value === undefined ? '' : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 0)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="isActive">
        {({ field }) => (
          <UzSwitch
            label="Active"
            description="Inactive statuses are hidden from follow-up forms."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
    </>
  );
};

export const CallStatusFormDrawer = ({
  open,
  onOpenChange,
  mode,
  callStatus,
}: CallStatusFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createCallStatus = useCreateCallStatus();
  const updateCallStatus = useUpdateCallStatus();
  const pending = createCallStatus.isPending || updateCallStatus.isPending;

  const createForm = useForm<CreateCallStatusRequest>({
    resolver: zodResolver(CreateCallStatusRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateCallStatusRequest>({
    resolver: zodResolver(UpdateCallStatusRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (callStatus) {
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        name: callStatus.name,
        description: callStatus.description ?? '',
        sortOrder: callStatus.sortOrder,
        isActive: callStatus.isActive,
      });
    }
  }, [open, isCreate, callStatus, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field).
    await createCallStatus.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!callStatus) return;
    await updateCallStatus.mutateAsync({
      id: callStatus.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <CallStatusFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add call status' : `Edit ${callStatus?.name ?? 'call status'}`}
      description="The name is what agents see."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
